import { Button, message } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { history, useModel } from "@umijs/max";
import dayjs from "dayjs";
import { useCookieState, useRequest } from "ahooks";
import styles from "./index.less";

import { ReactComponent as PrimaryInfoIcon } from "@/assets/images/primary-info.svg";
import { ReactComponent as FoldIcon } from "@/assets/images/fold.svg";

import PlanExpired from "@packages/pricing-components/src/components/PlanExpired";
import {
  isPredefinedVersion,
  PlanPayModalTypeEnum,
  PricingPeriodEnum,
  PricingPlanVersionEnum,
  PricingPlanVersionTextMap,
} from "@packages/pricing-components";
import PlanPayModal from "@packages/pricing-components/src/modals/PlanPayModal";
import { PayType } from "@/services/order";
import { getCustomPlanService } from "@packages/pricing-components/src/service";
import { ResponseCode } from "@/constants";
import { transformPlanResponseToVersionWithPriceMap } from "@packages/pricing-components/src/utils";

// 禁用逻辑
const disableScroll = () => {
  // 阻止滚轮事件
  document.addEventListener("wheel", preventScroll, { passive: false });
  // 阻止触摸事件（移动端）
  document.addEventListener("touchmove", preventScroll, { passive: false });
  // 阻止键盘方向键/空格键
  document.addEventListener("keydown", preventKeyScroll);
};

// 恢复逻辑
const enableScroll = () => {
  document.removeEventListener("wheel", preventScroll);
  document.removeEventListener("touchmove", preventScroll);
  document.removeEventListener("keydown", preventKeyScroll);
};

// 事件处理器
const preventScroll = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

const preventKeyScroll = (e: KeyboardEvent) => {
  const keys = ["Space", "ArrowUp", "ArrowDown", "PageUp", "PageDown"];
  if (keys.includes(e.code)) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
};

interface ExpireNoticeAndBannedMaskProps {
  isExcludeBannedMask?: boolean;
}

const ExpireNoticeAndBannedMask: FC<ExpireNoticeAndBannedMaskProps> = ({
  isExcludeBannedMask = false,
}) => {
  const { initialState } = useModel("@@initialState");
  const userPlanSubscriptionInformation =
    initialState?.userPlanSubscriptionInformation;

  const [isFold, setIsFold] = useCookieState("is-expire-notice-fold", {
    defaultValue: "",
    expires: dayjs(dayjs().format("YYYY-MM-DD")).add(1, "day").toDate(),
  });

  const [openPayModal, setOpenPayModal] = useState<boolean>(false);

  useEffect(() => {
    if (userPlanSubscriptionInformation?.isExpired && !isExcludeBannedMask) {
      disableScroll();

      return () => {
        enableScroll();
      };
    }
  }, [userPlanSubscriptionInformation?.isExpired, isExcludeBannedMask]);

  const { data: customPlanMap } = useRequest(
    async () => {
      const rsp = await getCustomPlanService();

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }

      return transformPlanResponseToVersionWithPriceMap(rsp.data)[
        userPlanSubscriptionInformation?.paidVersion ??
          PricingPlanVersionEnum.BASIC
      ];
    },
    {
      ready: !isPredefinedVersion(userPlanSubscriptionInformation?.paidVersion),
    }
  );

  const handleRenew = () => {
    // 自定义套餐续费
    if (!isPredefinedVersion(userPlanSubscriptionInformation?.paidVersion)) {
      setOpenPayModal(true);
      return;
    }

    // 非自定义套餐
    history.push("/pricing", {
      action: "renew",
    });
  };

  const versionText = useMemo(() => {
    if (!isPredefinedVersion(userPlanSubscriptionInformation?.paidVersion)) {
      return userPlanSubscriptionInformation?.paidVersion;
    }

    return PricingPlanVersionTextMap[
      userPlanSubscriptionInformation?.paidVersion ??
        PricingPlanVersionEnum.BASIC
    ];
  }, [userPlanSubscriptionInformation?.paidVersion]);

  return (
    <>
      <div className={styles["expire-notice"]}>
        <div className={styles.bar} onClick={() => setIsFold("")}></div>

        {!isFold && (
          <div className={styles.info}>
            <div className={styles.left}>
              <PrimaryInfoIcon />
              您订阅的 12Times ·&nbsp;
              {versionText}
              &nbsp;版将于&nbsp;
              {userPlanSubscriptionInformation?.expireAt}
              &nbsp;到期，
              {!userPlanSubscriptionInformation?.trailPlan
                ? "逾期未续费将影响您的正常使用"
                : "请联系管理员购买套餐"}
            </div>

            <div className={styles.right}>
              {!userPlanSubscriptionInformation?.trailPlan && (
                <Button type="primary" onClick={handleRenew}>
                  立即续费
                </Button>
              )}
              <Button
                type="text"
                icon={<FoldIcon />}
                onClick={() => setIsFold("true")}
              />
            </div>
          </div>
        )}
      </div>

      {userPlanSubscriptionInformation?.isExpired && !isExcludeBannedMask && (
        <div className={styles["banned-mask"]}>
          <PlanExpired
            expiredVersion={
              userPlanSubscriptionInformation?.paidVersion ??
              PricingPlanVersionEnum.BASIC
            }
            expiredDate={userPlanSubscriptionInformation?.expireAt}
            onRenew={handleRenew}
            trailPlan={userPlanSubscriptionInformation?.trailPlan ?? false}
          />
        </div>
      )}

      <PlanPayModal
        open={openPayModal}
        title={`续费定制套餐 - ${userPlanSubscriptionInformation?.paidVersion}`}
        type={PlanPayModalTypeEnum.Renew}
        allowedMethods={[
          PayType.WECHAT,
          PayType.ALIPAY,
          PayType.OFFLINE,
          PayType.BALANCE,
        ]}
        priceInformation={customPlanMap}
        onClose={() => {
          setOpenPayModal(false);
        }}
        initialValues={{
          period: PricingPeriodEnum.MONTHLY,
          planNum: 1,
          payMethod: PayType.WECHAT,
        }}
      />
    </>
  );
};

export default ExpireNoticeAndBannedMask;
